import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'
import { Marquee, Spin } from '@/components/anim'

const clientLogos = [1, 2, 3, 4, 5, 6]

const LogoRow: FC = () => (
    <div className='trusted-by-grid'>
        {clientLogos.map((n) => (
            <div className='trusted-by-grid-content' key={n}>
                <img src={`/images/Logo${n}.svg`} loading='lazy' alt='' className='client-logo' />
            </div>
        ))}
    </div>
)

/** Splits a word into individually animatable, inline-block character spans. */
const splitChars = (text: string): ReactNode =>
    text.split('').map((char, index) => (
        <span className='hero-char' aria-hidden='true' key={`${char}-${index}`}>
            {char}
        </span>
    ))

export const Hero: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        registerGsap()
        if (prefersReducedMotion() || isCompactViewport()) return

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

            tl.fromTo(
                '.hero-link',
                { opacity: 0, y: 22, scale: 0.96 },
                { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(1.5)' }
            )
                .fromTo(
                    '.hero-char',
                    { opacity: 0, yPercent: 60, rotateX: -55 },
                    {
                        opacity: 1,
                        yPercent: 0,
                        rotateX: 0,
                        duration: 0.65,
                        ease: 'back.out(1.6)',
                        stagger: 0.026
                    },
                    '-=0.35'
                )
                .fromTo(
                    '.hero-word',
                    { opacity: 0, yPercent: 60 },
                    { opacity: 1, yPercent: 0, duration: 0.7, ease: 'back.out(1.6)' },
                    '<0.25'
                )
                .fromTo(
                    '.hero-description',
                    { opacity: 0, y: 18, filter: 'blur(10px)' },
                    { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8 },
                    '-=0.35'
                )
                .fromTo(
                    '.subscribe-form-holder',
                    { opacity: 0, y: 18, scale: 0.985 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.7 },
                    '-=0.5'
                )
                .fromTo(
                    '.trusted-by-container',
                    { opacity: 0, y: 18 },
                    { opacity: 1, y: 0, duration: 0.7 },
                    '-=0.45'
                )
        }, rootRef)

        return () => ctx.revert()
    }, [])

    return (
        <div className='hero-section' ref={rootRef}>
            <div className='container'>
                <div className='hero-content'>
                    <div className='flex-center-text'>
                        <a href='/#product' className='hero-link w-inline-block' data-variant='move'>
                            <div className='hero-link-gradient'>
                                <div>New</div>
                            </div>
                            <div>Introducing real-time dashboards</div>
                            <div className='hero-link-arrow' />
                        </a>

                        <h1 className='hero-text' aria-label='Real-time performance management'>
                            <span className='hero-word-wrap' aria-hidden='true'>
                                {splitChars('Real-time')}
                            </span>
                            <br aria-hidden='true' />
                            <span className='gradient-text hero-word' aria-hidden='true'>
                                performance
                            </span>{' '}
                            <span className='hero-word-wrap' aria-hidden='true'>
                                {splitChars('management')}
                            </span>
                        </h1>

                        <div className='hero-description js-reveal' data-variant='move'>
                            From financial metrics to sales and marketing data, our dashboard provides
                            you with a holistic view of your business operations.
                        </div>

                        <div className='subscribe-form-holder w-form js-reveal' data-variant='move'>
                            {submitted ? (
                                <div className='success-message w-form-done' style={{ display: 'block' }}>
                                    <div className='gradient-text'>Thank you! We got you!</div>
                                </div>
                            ) : (
                                <form
                                    className='subscribe-form'
                                    onSubmit={(event) => {
                                        event.preventDefault()
                                        const email = new FormData(event.currentTarget).get('email')
                                        setSubmitted(true)
                                        void fetch('/api/subscribe', {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ email })
                                        }).catch(() => undefined)
                                    }}
                                >
                                    <input
                                        className='subscribe-email w-input'
                                        maxLength={256}
                                        name='email'
                                        placeholder='Your mail address'
                                        type='email'
                                        required
                                        aria-label='Your mail address'
                                    />
                                    <div className='form-button-holder'>
                                        <input
                                            type='submit'
                                            className='form-submit-button w-button'
                                            value='Get Access'
                                        />
                                        <div className='add-to-cart-button-border-holder'>
                                            <div className='add-to-cart-button-border' />
                                        </div>
                                        <div className='add-to-cart-button-gradient' />
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>

                <div className='trusted-by-container js-reveal' data-variant='move'>
                    <Marquee className='trusted-grid-holder'>
                        <LogoRow />
                        <LogoRow />
                    </Marquee>
                    <div className='trusted-by-text'>Trusted by top-tier product companies</div>
                </div>
            </div>

            <div className='gradient-center-holder'>
                <Spin as='div' className='gradient-center' duration={30} />
            </div>
        </div>
    )
}
